import mapboxgl, { MapMouseEvent } from 'mapbox-gl';
import * as turf from '@turf/turf';

export const MEASURE_POINTS_LAYER_ID = 'measure-points';
export const MEASURE_LINES_LAYER_ID = 'measure-lines';
export const MEASURE_POLYGONS_LAYER_ID = 'measure-polygons';
export const MEASURE_LABELS_LAYER_ID = 'measure-labels';

export const initializeMapLayers = (map: mapboxgl.Map) => {
  if (!map.getSource(MEASURE_POINTS_LAYER_ID)) {
    map.addSource(MEASURE_POINTS_LAYER_ID, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });

    map.addLayer({
      id: MEASURE_POINTS_LAYER_ID,
      type: 'circle',
      source: MEASURE_POINTS_LAYER_ID,
      paint: {
        'circle-radius': 5,
        'circle-color': '#000'
      }
    });

    map.addLayer({
      id: MEASURE_LINES_LAYER_ID,
      type: 'line',
      source: MEASURE_POINTS_LAYER_ID,
      layout: {
        'line-cap': 'round',
        'line-join': 'round'
      },
      paint: {
        'line-color': '#000',
        'line-width': 2.5
      },
      filter: ['==', '$type', 'LineString']
    });

    map.addLayer({
      id: MEASURE_POLYGONS_LAYER_ID,
      type: 'fill',
      source: MEASURE_POINTS_LAYER_ID,
      layout: {},
      paint: {
        'fill-color': '#000',
        'fill-opacity': 0.3
      },
      filter: ['==', '$type', 'Polygon']
    });

    map.addSource(MEASURE_LABELS_LAYER_ID, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });

    map.addLayer({
      id: MEASURE_LABELS_LAYER_ID,
      type: 'symbol',
      source: MEASURE_LABELS_LAYER_ID,
      layout: {
        'text-field': ['get', 'label'],
        'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-offset': [0, 1.5],
        'text-anchor': 'top'
      },
      paint: {
        'text-color': '#000',
        'text-halo-color': '#fff',
        'text-halo-width': 2
      }
    });
  }
};

export const handleClick = (
  e: MapMouseEvent,
  map: mapboxgl.Map,
  drawnFeatures: GeoJSON.Feature<GeoJSON.Point>[],
  setDrawnFeatures: React.Dispatch<React.SetStateAction<GeoJSON.Feature<GeoJSON.Point>[]>>,
  segmentLabels: GeoJSON.Feature<GeoJSON.Point>[],
  setSegmentLabels: React.Dispatch<React.SetStateAction<GeoJSON.Feature<GeoJSON.Point>[]>>,
  setMeasurements: React.Dispatch<React.SetStateAction<{ distanceKm: number; distanceNm: number; areaKm2: number; areaM2: number; areaHa: number }>>
) => {
  const point: GeoJSON.Feature<GeoJSON.Point> = {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [e.lngLat.lng, e.lngLat.lat]
    },
    properties: {}
  };

  const newDrawnFeatures = [...drawnFeatures, point];
  setDrawnFeatures(newDrawnFeatures);

  const featureCollection: GeoJSON.FeatureCollection = {
    type: 'FeatureCollection',
    features: [...newDrawnFeatures]
  };

  const newSegmentLabels = [...segmentLabels];

  if (newDrawnFeatures.length > 1) {
    const line: GeoJSON.Feature<GeoJSON.LineString> = {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: newDrawnFeatures.map(feature => (feature.geometry as GeoJSON.Point).coordinates)
      },
      properties: {}
    };

    featureCollection.features.push(line);

    const distanceKm = turf.length(line, { units: 'kilometers' });
    const distanceNm = distanceKm * 0.539957;
    setMeasurements({ distanceKm, distanceNm, areaKm2: 0, areaM2: 0, areaHa: 0 });

    // Calculate segment distance and bearing
    const from = newDrawnFeatures[newDrawnFeatures.length - 2].geometry.coordinates as [number, number];
    const to = newDrawnFeatures[newDrawnFeatures.length - 1].geometry.coordinates as [number, number];
    const segmentDistanceKm = turf.distance(from, to, { units: 'kilometers' });
    const segmentDistanceNm = segmentDistanceKm * 0.539957;
    let bearing = turf.bearing(from, to);
    if (bearing < 0) bearing += 360;

    const label: GeoJSON.Feature<GeoJSON.Point> = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: to
      },
      properties: {
        label: `${segmentDistanceKm.toFixed(2)} km / ${segmentDistanceNm.toFixed(2)} nm\nBearing: ${bearing.toFixed(2)}°`
      }
    };

    newSegmentLabels.push(label);
    setSegmentLabels(newSegmentLabels);
  }

  if (newDrawnFeatures.length > 2) {
    const polygon: GeoJSON.Feature<GeoJSON.Polygon> = {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [[...newDrawnFeatures.map(feature => (feature.geometry as GeoJSON.Point).coordinates), (newDrawnFeatures[0].geometry as GeoJSON.Point).coordinates]]
      },
      properties: {}
    };

    featureCollection.features.push(polygon);

    const areaM2 = turf.area(polygon);
    const areaKm2 = areaM2 / 1000000;
    const areaHa = areaKm2 * 100;
    setMeasurements(prev => ({ ...prev, areaKm2, areaM2, areaHa }));
  }

  (map.getSource(MEASURE_POINTS_LAYER_ID) as mapboxgl.GeoJSONSource).setData(featureCollection);

  const measureLabelsSource = map.getSource(MEASURE_LABELS_LAYER_ID) as mapboxgl.GeoJSONSource;
  if (measureLabelsSource) {
    measureLabelsSource.setData({
      type: 'FeatureCollection',
      features: newSegmentLabels
    });
  }
};

